<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import type { DesignItem } from '@core/types';
import { t } from './i18n';
import {
  apiToken,
  designerBaseUrl,
  request,
  stockRateLimitData,
  type ImportedStockItem,
  type ProviderId,
  type ProviderState,
  type StockImageResult,
} from './stockApi';

const props = defineProps<{ context?: { config?: Record<string, unknown> } }>();

const q = ref('');
const providers = ref<ProviderState>({
  providers: { openverse: true, pixabay: false },
  configuredProviders: { openverse: true, pixabay: true },
  hasPixabayKey: false,
  customerDirectSearch: false,
});
const activeProvider = ref<ProviderId>('openverse');
const items = ref<StockImageResult[]>([]);
const page = ref(1);
const total = ref(0);
const loading = ref(false);
const loadingMore = ref(false);
const importingId = ref('');
const selected = ref<StockImageResult | null>(null);
const error = ref('');
const pixabayRateLimited = ref(false);
const pixabayRetryAfter = ref(0);
let timer: number | null = null;
let rateTimer: number | null = null;
let searchController: AbortController | null = null;
let requestToken = 0;

const baseUrl = computed(() => designerBaseUrl(props.context));
const enabledProviders = computed(() => (Object.entries(providers.value.providers) as [ProviderId, boolean][])
  .filter(([, enabled]) => enabled)
  .map(([id]) => id));
const directSearchEnabled = computed(() => Boolean(providers.value.customerDirectSearch));
const hasMore = computed(() => items.value.length < total.value);
const pixabayCooldownActive = computed(() => activeProvider.value === 'pixabay' && pixabayRetryAfter.value > 0);
const emptyMessage = computed(() => {
  if (directSearchEnabled.value && !enabledProviders.value.length) return t('No stock image providers are enabled.');
  if (directSearchEnabled.value && !q.value.trim()) return t('Search a provider to find photos.');
  if (!directSearchEnabled.value && !q.value.trim()) return t('No curated stock images are available yet.');
  return t('No images matched your search.');
});

function providerLabel(provider: ProviderId): string {
  return provider === 'openverse' ? 'Openverse' : 'Pixabay';
}

function startPixabayCooldown(seconds: number) {
  pixabayRateLimited.value = true;
  pixabayRetryAfter.value = Math.max(1, Math.ceil(seconds));
  if (rateTimer) window.clearInterval(rateTimer);
  rateTimer = window.setInterval(() => {
    pixabayRetryAfter.value = Math.max(0, pixabayRetryAfter.value - 1);
    if (pixabayRetryAfter.value === 0 && rateTimer) {
      window.clearInterval(rateTimer);
      rateTimer = null;
    }
  }, 1000);
}

function retryPixabay() {
  pixabayRateLimited.value = false;
  void search(true);
}

async function loadProviders() {
  try {
    providers.value = await request<ProviderState>(baseUrl.value, '/api/v1/stock_images/providers');
    if (!providers.value.providers[activeProvider.value]) {
      activeProvider.value = enabledProviders.value[0] ?? 'openverse';
    }
  } catch (err) {
    error.value = err instanceof Error ? err.message : t('Could not load stock providers.');
  }
}

async function search(reset = true) {
  const term = q.value.trim();
  if (!directSearchEnabled.value && !term) {
    // Empty local library search lists curated stock items.
  } else if (!term || (directSearchEnabled.value && !providers.value.providers[activeProvider.value])) {
    items.value = [];
    total.value = 0;
    return;
  }
  if (pixabayCooldownActive.value) return;
  const token = ++requestToken;
  searchController?.abort();
  searchController = new AbortController();
  if (reset) {
    page.value = 1;
    loading.value = true;
  } else {
    loadingMore.value = true;
  }
  error.value = '';
  try {
    const params = directSearchEnabled.value
      ? new URLSearchParams({ provider: activeProvider.value, q: term, page: String(page.value) })
      : new URLSearchParams({ q: term, page: String(page.value) });
    const path = directSearchEnabled.value ? '/api/v1/stock_images/search' : '/api/v1/stock_images/library';
    const data = await request<{ items: StockImageResult[]; total: number; cached?: boolean; stale?: boolean; retryAfter?: number }>(baseUrl.value, `${path}?${params.toString()}`, { signal: searchController.signal });
    if (token !== requestToken) return;
    items.value = reset ? data.items : [...items.value, ...data.items];
    total.value = Number(data.total) || items.value.length;
    if (activeProvider.value === 'pixabay' && Number(data.retryAfter) > 0) startPixabayCooldown(Number(data.retryAfter));
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') return;
    const rateLimit = stockRateLimitData(err);
    if (rateLimit) startPixabayCooldown(rateLimit.retryAfter);
    if (token === requestToken) error.value = err instanceof Error ? err.message : t('Search failed.');
  } finally {
    if (token === requestToken) {
      loading.value = false;
      loadingMore.value = false;
    }
  }
}

function queueSearch() {
  if (timer) window.clearTimeout(timer);
  timer = window.setTimeout(() => {
    timer = null;
    void search(true);
  }, 600);
}

function switchProvider(provider: ProviderId) {
  activeProvider.value = provider;
  items.value = [];
  total.value = 0;
  void search(true);
}

async function loadMore() {
  if (loading.value || loadingMore.value || !hasMore.value) return;
  page.value += 1;
  await search(false);
}

function resolveUrl(url: string): string {
  if (/^https?:\/\//i.test(url)) return url;
  return new URL(url, window.location.origin).toString();
}

function imageDimensions(urls: string[]): Promise<{ width: number; height: number } | null> {
  const unique = urls.filter(Boolean).filter((url, index, all) => all.indexOf(url) === index);
  return new Promise((resolve) => {
    let index = 0;
    const tryNext = () => {
      const url = unique[index++];
      if (!url) {
        resolve(null);
        return;
      }
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth || 0, height: img.naturalHeight || 0 });
      img.onerror = tryNext;
      img.src = url;
    };
    tryNext();
  });
}

function scaleToMax(width: number, height: number, max = 120): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (!longest) return { width: max, height: max };
  const scale = Math.min(1, max / longest);
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

function nextItemId(): string {
  return `stock-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

async function addImportedItem(imported: ImportedStockItem) {
  const original = resolveUrl(imported.original || imported.url);
  const medium = resolveUrl(imported.medium || imported.url);
  const thumb = resolveUrl(imported.thumb || imported.url);
  const canvasSource = original || medium;
  const dimensions = await imageDimensions([original, medium, thumb]);
  const box = dimensions ? scaleToMax(dimensions.width, dimensions.height) : { width: 120, height: 120 };
  const item: DesignItem = {
    id: nextItemId(),
    type: 'image',
    text: '',
    src: canvasSource,
    top: 50,
    left: 50,
    width: box.width,
    height: box.height,
    zIndex: 0,
    angle: 0,
    opacity: 1,
    upload: 1,
    file_name: imported.file_name,
    file: canvasSource,
    thumb,
    large: original,
    sourcePixelWidth: dimensions?.width ?? 0,
    sourcePixelHeight: dimensions?.height ?? 0,
    url: baseUrl.value,
    stockProvider: imported.stockProvider,
    stockAttribution: imported.stockAttribution,
    stockLicense: imported.stockLicense,
    stockLicenseUrl: imported.stockLicenseUrl,
    stockSourceUrl: imported.stockSourceUrl,
  };
  const veyop = (window as unknown as { Veyop?: { designer?: { commands?: { addItem?: (next: DesignItem, options?: { placement?: 'print-area-center' }) => void } }; ui?: { notify?: (type: 'success', message: string) => void } } }).Veyop;
  veyop?.designer?.commands?.addItem?.(item, { placement: 'print-area-center' });
  veyop?.ui?.notify?.('success', t('Stock image added to your design.'));
}

async function importImage(result: StockImageResult) {
  importingId.value = `${result.provider}:${result.id}`;
  error.value = '';
  try {
    const data = await request<{ item: ImportedStockItem }>(baseUrl.value, '/api/v1/stock_images/import', {
      method: 'POST',
      headers: { 'X-CSRF-Token': apiToken(props.context) },
      body: JSON.stringify({ provider: result.provider, id: result.id }),
    });
    await addImportedItem(data.item);
    selected.value = null;
  } catch (err) {
    error.value = err instanceof Error ? err.message : t('Could not import stock image.');
  } finally {
    importingId.value = '';
  }
}

watch(q, queueSearch);

function closePreview() {
  if (!importingId.value) selected.value = null;
}

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') closePreview();
}

onMounted(async () => {
  window.addEventListener('keydown', onKeydown);
  await loadProviders();
  void search(true);
});

onBeforeUnmount(() => {
  if (timer) window.clearTimeout(timer);
  if (rateTimer) window.clearInterval(rateTimer);
  searchController?.abort();
  window.removeEventListener('keydown', onKeydown);
});
</script>

<template>
  <div class="vptw:flex vptw:flex-col vptw:gap-3">
    <div v-if="directSearchEnabled" class="vptw:flex vptw:gap-1 vptw:rounded-lg vptw:bg-gray-100 vptw:p-1">
      <button
        v-for="provider in enabledProviders"
        :key="provider"
        type="button"
        class="vptw:min-h-9 vptw:flex-1 vptw:rounded-md vptw:border-0! vptw:px-2 vptw:text-xs vptw:font-semibold vptw:transition"
        :class="activeProvider === provider ? 'vptw:bg-white! vptw:text-(--vs-ink) vptw:shadow-sm' : 'vptw:bg-transparent! vptw:text-(--vs-ink-2)'"
        @click="switchProvider(provider)"
      >
        {{ providerLabel(provider) }}
      </button>
    </div>

    <input
      v-model="q"
      type="search"
      class="vptw:min-h-10 vptw:w-full vptw:rounded-lg vptw:border vptw:border-(--vs-line) vptw:bg-white vptw:px-3 vptw:text-sm"
      :placeholder="t('Search photos')"
      :disabled="pixabayCooldownActive"
    >

    <p v-if="activeProvider === 'pixabay' && pixabayRateLimited" class="vptw:m-0 vptw:rounded-lg vptw:bg-amber-50 vptw:p-3 vptw:text-xs vptw:text-amber-800">
      <template v-if="pixabayRetryAfter > 0">{{ t('Pixabay rate limit reached.') }} {{ t('Retry in') }} {{ pixabayRetryAfter }}s.</template>
      <button v-else type="button" class="vptw:ml-2 vptw:border-0! vptw:bg-transparent! vptw:p-0! vptw:font-semibold vptw:text-amber-900! vptw:cursor-pointer" @click="retryPixabay">{{ t('Retry Pixabay') }}</button>
    </p>

    <p v-if="directSearchEnabled && !enabledProviders.length" class="vptw:m-0 vptw:rounded-lg vptw:bg-amber-50 vptw:p-3 vptw:text-xs vptw:text-amber-800">
      {{ t('No stock image providers enabled.') }}
    </p>
    <p v-else-if="error && !(activeProvider === 'pixabay' && pixabayRateLimited)" class="vptw:m-0 vptw:rounded-lg vptw:bg-red-50 vptw:p-3 vptw:text-xs vptw:text-red-700">
      {{ error }}
    </p>

    <div v-if="loading" class="vptw:grid vptw:grid-cols-2 vptw:gap-2" aria-label="Loading">
      <div v-for="n in 6" :key="n" class="vptw:aspect-square vptw:animate-pulse vptw:rounded-lg vptw:bg-gray-100" />
    </div>
    <template v-else>
      <div class="vptw:grid vptw:grid-cols-2 vptw:gap-2">
        <template v-for="item in items" :key="`${item.provider}:${item.id}`">
          <button
            type="button"
            class="vptw:group vptw:overflow-hidden vptw:rounded-lg vptw:border vptw:border-(--vs-line) vptw:bg-white vptw:p-0! vptw:text-left vptw:shadow-sm vptw:transition vptw:hover:border-(--vs-accent)"
            :disabled="importingId === `${item.provider}:${item.id}`"
            @click="selected = item"
          >
            <span class="vptw:block vptw:aspect-square vptw:bg-gray-100">
              <img :src="item.preview || item.thumb" :alt="item.title" class="vptw:h-full vptw:w-full vptw:object-cover" loading="lazy">
            </span>
            <span class="vptw:block vptw:truncate vptw:px-2 vptw:pt-1.5 vptw:text-[10px] vptw:font-semibold vptw:text-(--vs-ink-2)">{{ item.title }}</span>
            <span class="vptw:block vptw:truncate vptw:px-2 vptw:pb-1.5 vptw:text-[10px] vptw:leading-tight vptw:text-(--vs-ink-2)" :title="`${item.attribution} · ${item.license}`">
              {{ item.attribution }} · {{ item.license }}
            </span>
            <span v-if="importingId === `${item.provider}:${item.id}`" class="vptw:block vptw:px-2 vptw:pb-2 vptw:text-[10px] vptw:font-semibold vptw:text-(--vs-accent)">
              {{ t('Importing...') }}
            </span>
          </button>
        </template>
        <p v-if="!items.length && !error" class="vptw:col-span-2 vptw:m-0 vptw:py-8 vptw:text-center vptw:text-xs vptw:text-(--vs-ink-2)">
          {{ emptyMessage }}
        </p>
      </div>
      <button
        v-if="hasMore"
        type="button"
        class="vptw:min-h-10 vptw:w-full vptw:rounded-lg vptw:border vptw:border-(--vs-line) vptw:bg-gray-900! vptw:px-3 vptw:text-xs vptw:font-semibold vptw:text-white!"
        :disabled="loadingMore || pixabayCooldownActive"
        @click="loadMore"
      >
        {{ loadingMore ? t('Loading...') : t('Load more') }}
      </button>
    </template>

    <Teleport to="body">
      <div v-if="selected" class="vptw:fixed vptw:inset-0 vptw:z-[100100] vptw:flex vptw:items-end vptw:justify-center vptw:bg-black/55 vptw:p-0 vptw:sm:items-center vptw:sm:p-4" role="dialog" aria-modal="true" :aria-label="t('Stock image preview')" @click.self="closePreview">
        <article class="vptw:flex vptw:max-h-[92vh] vptw:w-full vptw:max-w-2xl vptw:flex-col vptw:overflow-hidden vptw:rounded-t-2xl vptw:bg-white vptw:shadow-2xl vptw:sm:rounded-2xl">
          <header class="vptw:flex vptw:items-start vptw:justify-between vptw:gap-3 vptw:border-b vptw:border-(--vs-line) vptw:p-4">
            <div class="vptw:min-w-0">
              <h3 class="vptw:m-0 vptw:truncate vptw:text-base vptw:font-semibold vptw:text-(--vs-ink)">{{ selected.title }}</h3>
              <p class="vptw:m-0 vptw:mt-1 vptw:text-xs vptw:text-(--vs-ink-2)">{{ providerLabel(selected.provider) }}<template v-if="selected.width && selected.height"> · {{ selected.width }} × {{ selected.height }} px</template></p>
            </div>
            <button type="button" class="vptw:size-9 vptw:shrink-0 vptw:rounded-full vptw:border-0! vptw:bg-gray-100! vptw:text-lg vptw:cursor-pointer" :aria-label="t('Close preview')" :disabled="Boolean(importingId)" @click="closePreview">×</button>
          </header>
          <div class="vptw:min-h-0 vptw:overflow-y-auto">
            <div class="vptw:flex vptw:max-h-[52vh] vptw:min-h-64 vptw:items-center vptw:justify-center vptw:bg-gray-100 vptw:p-3">
              <img :src="selected.preview || selected.thumb" :alt="selected.title" class="vptw:max-h-[48vh] vptw:max-w-full vptw:object-contain">
            </div>
            <div class="vptw:space-y-2 vptw:p-4 vptw:text-xs vptw:leading-5 vptw:text-(--vs-ink-2)">
              <p class="vptw:m-0"><strong class="vptw:text-(--vs-ink)">{{ t('Attribution') }}:</strong> {{ selected.attribution }}</p>
              <p class="vptw:m-0"><strong class="vptw:text-(--vs-ink)">{{ t('License') }}:</strong> <a v-if="selected.licenseUrl" :href="selected.licenseUrl" target="_blank" rel="noopener noreferrer" class="vptw:text-(--vs-accent)">{{ selected.license }}</a><span v-else>{{ selected.license }}</span></p>
              <p v-if="selected.sourceUrl" class="vptw:m-0"><a :href="selected.sourceUrl" target="_blank" rel="noopener noreferrer" class="vptw:text-(--vs-accent)">{{ t('Open original source') }} ↗</a></p>
            </div>
          </div>
          <footer class="vptw:border-t vptw:border-(--vs-line) vptw:p-4">
            <button type="button" class="vptw:min-h-11 vptw:w-full vptw:rounded-xl vptw:border-0! vptw:bg-gray-900! vptw:px-4 vptw:text-sm vptw:font-semibold vptw:text-white! vptw:cursor-pointer vptw:disabled:opacity-50" :disabled="Boolean(importingId)" @click="importImage(selected)">
              {{ importingId ? t('Importing...') : t('Add to design') }}
            </button>
          </footer>
        </article>
      </div>
    </Teleport>
  </div>
</template>
